import * as Lib from "../constants/annotator"
import { DEFAULT_MEDIAN_BEAT_WINDOW_MS } from "../constants/annotator"
import ComputeAvgMedBeat from "./a&mPlot"
import calculateLeads from "./leadCalc"
import applyInversion from "./applyInversion"
import { resampleSignal } from "./resample"

// Sentinel key used to attach the RMS composite trace to a lead-keyed map.
// Shared between the display views and the save-time serializer so both use
// the same reserved key.
export const MEDIAN_BEAT_RMS_KEY = "__rms__"

// Map kai12Analysis.medianBeats (an 8-lead array at MEDIAN_BEAT_FREQUENCY_12_LEAD)
// into a lead-keyed dict resampled to MEDIAN_BEAT_TARGET_HZ, with the four
// derived limb leads (III, aVR, aVL, aVF) filled in via calculateLeads.
//
// The kai12 array index → lead mapping is a contract with the upstream
// pipeline; the only in-repo record of it lives here (and previously in the
// two MedianBeats views this replaces). Slot 2/3 is real (V1/V2) or
// synthesized (V1-syn/V2-syn) depending on whether the source recording
// exposed a real V1 signal.
const build12LeadWaveforms = (annotatorState) => {
    const kai12 = annotatorState?.rawData?.data?.kai12Analysis
    if (!kai12 || !Array.isArray(kai12.medianBeats) || kai12.medianBeats.length < 8) return null

    let mBGraph = {}
    mBGraph.I = kai12.medianBeats[0]
    mBGraph.II = kai12.medianBeats[1]
    mBGraph = calculateLeads(mBGraph)
    if (annotatorState?.original?.samples?.V1) {
        mBGraph.V1 = kai12.medianBeats[2]
        mBGraph["V2-syn"] = kai12.medianBeats[3]
    } else {
        mBGraph["V1-syn"] = kai12.medianBeats[2]
        mBGraph.V2 = kai12.medianBeats[3]
    }
    mBGraph["V3-syn"] = kai12.medianBeats[4]
    mBGraph.V4 = kai12.medianBeats[5]
    mBGraph["V5-syn"] = kai12.medianBeats[6]
    mBGraph["V6-syn"] = kai12.medianBeats[7]

    // Upsample each lead to the common target rate so fiducial-point
    // placement/interval math has sub-2ms resolution regardless of source.
    Object.keys(mBGraph).forEach(leadName => {
        mBGraph[leadName] = resampleSignal(mBGraph[leadName], Lib.MEDIAN_BEAT_FREQUENCY_12_LEAD, Lib.MEDIAN_BEAT_TARGET_HZ)
    })
    return { waveforms: mBGraph, renderRate: Lib.MEDIAN_BEAT_TARGET_HZ }
}

// Compute per-lead median-beat waveforms for one beat-type slug on a
// non-12-lead recording. Mirrors the two MedianBeats views' inner loops:
// apply inversion, run ComputeAvgMedBeat per lead using the beat-type's
// window (or the default), then resample lower-rate recordings up to the
// common target so pixel↔sample math has consistent precision. Higher-rate
// recordings (e.g. 750 Hz) are left alone — downsampling would lose precision.
const build1Or6LeadWaveforms = (annotatorState, slug, { requireComputable }) => {
    const filtered = annotatorState?.filtered
    if (!filtered?.samples) return null
    const freq = filtered.frequency
    if (!freq) return null
    const inverted = !!annotatorState?.settings?.inverted
    const samples = inverted ? applyInversion(filtered.samples) : filtered.samples

    const windowForType = (annotatorState?.settings?.medianBeatWindowByType || {})[slug]
    const leftMs = windowForType?.leftMs ?? DEFAULT_MEDIAN_BEAT_WINDOW_MS
    const rightMs = windowForType?.rightMs ?? DEFAULT_MEDIAN_BEAT_WINDOW_MS
    const leftSamples = Math.round((leftMs / 1000) * freq)
    const rightSamples = Math.round((rightMs / 1000) * freq)
    const renderRate = freq < Lib.MEDIAN_BEAT_TARGET_HZ ? Lib.MEDIAN_BEAT_TARGET_HZ : freq

    const out = {}
    for (const leadLabel of Object.keys(samples)) {
        const raw = ComputeAvgMedBeat(
            annotatorState.annotations.beats,
            samples[leadLabel],
            [],
            true,
            leftSamples,
            rightSamples,
            slug,
        )
        // ComputeAvgMedBeat returns [] when there are <2 beats matching the
        // filter. Views want to render an empty tile; the save path wants to
        // skip the beat type entirely (see requireComputable).
        if ((!raw || raw.length === 0) && requireComputable) return null
        out[leadLabel] = renderRate === freq ? raw : resampleSignal(raw, freq, renderRate)
    }
    return { waveforms: out, renderRate }
}

// Public: compute per-lead median-beat waveforms for the given beat-type slug.
//
// Returns:
//   { waveforms: { [leadName]: number[] }, renderRate }  on success
//   null                                                 if the recording has
//     no computable median for this slug (12-lead: kai12 missing; 1/6-lead
//     with requireComputable: fewer than 2 matching beats)
//
// Note: the returned map does NOT include an RMS trace. Callers that need it
// should compute it with computeRmsWaveform over the returned waveforms and
// attach it under MEDIAN_BEAT_RMS_KEY. This mirrors how the views handle RMS
// (a separate useMemo alongside setThreeRowSamples).
//
// `requireComputable` (default true) controls empty-lead handling: when false,
// non-12-lead recordings pass through empty per-lead arrays instead of
// returning null. The view components pass false so they can still render a
// tile grid before beat annotations are placed; the save serializer leaves it
// at true to skip beat types with too few beats.
export const computeMedianBeatWaveforms = (
    annotatorState,
    { slug, requireComputable = true } = {},
) => {
    if (!annotatorState) return null
    const is12Lead = annotatorState.leadCount === 12
    if (is12Lead) {
        // kai12 median beats aren't split by beat type; the same waveforms
        // apply regardless of slug. Callers that want to serialize under a
        // specific bucket (e.g. only `n`) filter at the call site.
        return build12LeadWaveforms(annotatorState)
    }
    if (!slug) return null
    return build1Or6LeadWaveforms(annotatorState, slug, { requireComputable })
}
